import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';

import usePartnerAuth from 'hooks/usePartnerAuth';
import peryaplayLogo from 'assets/images/perya-play-logo.png';

/**
 * Full-page partner login, shown as the default landing for unauthenticated
 * users when VITE_APP_REQUIRE_LOGIN is enabled (see PartnerLayout). Matches the
 * "Welcome back" design (SO ID + password). Reuses the same auth flow as the
 * login modal (usePartnerAuth().login).
 */
const PartnerLoginPage = () => {
    const { mpId = '' } = useParams();
    const { login } = usePartnerAuth();
    const theme = useTheme();
    const gold = theme.palette.secondary.main;

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSubmitting(true);
        const result = await login(username.trim(), password, mpId);
        setSubmitting(false);
        if (result.error) setError(result.error);
    };

    const fieldWrap = {
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        backgroundColor: '#181818',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '14px',
        px: 2,
        height: 56,
        transition: 'border-color .2s',
        '&:focus-within': { borderColor: gold }
    } as const;

    return (
        <Box
            sx={{
                minHeight: '100vh',
                backgroundColor: '#121212',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    flex: 1,
                    width: '100%',
                    maxWidth: 460,
                    mx: 'auto',
                    px: 3,
                    pt: { xs: 8, sm: 12 },
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {/* Logo + heading */}
                <Stack alignItems="center" spacing={1.5} sx={{ mb: 5 }}>
                    {/* <Box component="img" src={peryaplayLogo} alt="SSO Network" sx={{ height: 64, mb: 1 }} /> */}
                    <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '28px', lineHeight: 1.1 }}>Welcome To</Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '15px' }}>MY SSO NETWORK</Typography>
                </Stack>

                {/* SO ID */}
                <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', mb: 1 }}>SO ID</Typography>
                <Box sx={{ ...fieldWrap, mb: 2.5 }}>
                    <PersonOutlineRoundedIcon sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 22 }} />
                    <InputBase
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="Enter SO ID"
                        autoFocus
                        required
                        sx={{ flex: 1, color: '#fff', fontWeight: 700, fontSize: '16px', '& input::placeholder': { color: 'rgba(255,255,255,0.4)', fontWeight: 500 } }}
                    />
                </Box>

                {/* Password */}
                <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', mb: 1 }}>Password</Typography>
                <Box sx={{ ...fieldWrap }}>
                    <LockOutlinedIcon sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 22 }} />
                    <InputBase
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter password"
                        type={showPassword ? 'text' : 'password'}
                        required
                        sx={{ flex: 1, color: '#fff', fontWeight: 700, fontSize: '16px', '& input::placeholder': { color: 'rgba(255,255,255,0.4)', fontWeight: 500 } }}
                    />
                    <IconButton onClick={() => setShowPassword((s) => !s)} edge="end" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                        {showPassword ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                    </IconButton>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}

                <Button
                    type="submit"
                    disabled={submitting}
                    sx={{
                        mt: 4,
                        height: 56,
                        borderRadius: '14px',
                        backgroundColor: gold,
                        color: '#1a1a1a',
                        fontWeight: 800,
                        fontSize: '17px',
                        textTransform: 'none',
                        boxShadow: `0 0 24px ${gold}66`,
                        '&:hover': { backgroundColor: gold, opacity: 0.92 },
                        '&.Mui-disabled': { backgroundColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.35)', boxShadow: 'none' }
                    }}
                >
                    {submitting ? 'Signing in…' : 'Sign In'}
                </Button>
            </Box>

            {/* Footer */}
            <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', py: 2.5, textAlign: 'center' }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '14px' }}>
                    Powered by <Box component="span" sx={{ color: '#fff', fontWeight: 800 }}>SSO.</Box>
                </Typography>
            </Box>
        </Box>
    );
};

export default PartnerLoginPage;
